import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Home,
    Users,
    Calendar,
    CalendarClock,
    CalendarCheck,
    Mic,
    Award,
    MapPin,
    MessageCircle,
    Camera,
    FileText,
    Heart,
    Handshake,
    QrCode
} from 'lucide-react';
import { apiService } from '../services/api';
import './EventNavigation.css';

const EventNavigation = ({ eventNameId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingMeetingsCount, setPendingMeetingsCount] = useState(0);
    const [eventId, setEventId] = useState(null);
    const [icEventId, setIcEventId] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const eventIdRef = useRef(null);
    const icEventIdRef = useRef(null);
    const pollIntervalRef = useRef(null);

    // Fetch event data to get EventId and ic_event_id for filtering
    useEffect(() => {
        const fetchEventId = async () => {
            if (eventNameId) {
                try {
                    const response = await apiService.getEventByNameId(eventNameId);
                    if (response.data?.EventId) {
                        setEventId(response.data.EventId);
                    }
                    if (response.data?.ic_event_id) {
                        setIcEventId(response.data.ic_event_id);
                    }
                } catch (err) {
                    console.error('Error fetching event for navigation:', err);
                }
            }
        };
        fetchEventId();
    }, [eventNameId]);

    useEffect(() => {
        if (eventNameId) {
            fetchAccessiblePages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Keep refs in sync with state
    useEffect(() => {
        eventIdRef.current = eventId;
    }, [eventId]);

    useEffect(() => {
        icEventIdRef.current = icEventId;
    }, [icEventId]);

    // Fetch unread count and pending meetings count when eventId is available
    useEffect(() => {
        if (eventId) {
            fetchUnreadCount();
            startUnreadPolling();
        }
        return () => stopUnreadPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    // Fetch pending meetings count when both eventId and icEventId are available
    useEffect(() => {
        if (eventId && icEventId) {
            fetchPendingMeetingsCount();
        }
    }, [eventId, icEventId]);

    // Listen for custom event to refresh unread count (triggered from EventMessages)
    useEffect(() => {
        const handleRefreshUnread = () => {
            fetchUnreadCount();
        };
        window.addEventListener('refreshUnreadCount', handleRefreshUnread);
        return () => window.removeEventListener('refreshUnreadCount', handleRefreshUnread);
    }, []);

    // Listen for custom event to refresh pending meetings count (triggered from EventMeetings)
    useEffect(() => {
        const handleRefreshMeetings = () => {
            fetchPendingMeetingsCount();
        };
        window.addEventListener('refreshPendingMeetingsCount', handleRefreshMeetings);
        return () => window.removeEventListener('refreshPendingMeetingsCount', handleRefreshMeetings);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            // Use ref to always get the current eventId (important for polling)
            const currentEventId = eventIdRef.current;
            if (!currentEventId) {
                return; // Don't fetch if we don't have an eventId yet
            }
            // Pass eventId to only count unread from F4C attendee chats
            const response = await apiService.getICUnreadCount(currentEventId);
            if (response.data?.success) {
                setUnreadCount(response.data.count || 0);
            }
        } catch (err) {
            // Silent fail - don't break nav if messaging fails
            console.error('Error fetching unread count:', err);
        }
    };

    const fetchPendingMeetingsCount = async () => {
        try {
            const currentEventId = eventIdRef.current;
            const currentIcEventId = icEventIdRef.current;
            if (!currentEventId || !currentIcEventId) {
                return;
            }
            const response = await apiService.getPendingMeetingsCount(currentEventId, currentIcEventId);
            if (response.data?.success) {
                setPendingMeetingsCount(response.data.count || 0);
            }
        } catch (err) {
            // Silent fail - don't break nav if meetings count fails
            console.error('Error fetching pending meetings count:', err);
        }
    };

    const startUnreadPolling = useCallback(() => {
        if (pollIntervalRef.current) return;

        pollIntervalRef.current = setInterval(() => {
            fetchUnreadCount();
        }, 60000); // Poll every 60 seconds
    }, []);

    const stopUnreadPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const fetchAccessiblePages = async () => {
        try {
            setLoading(true);
            const response = await apiService.getAccessibleEventPages(eventNameId);
            setPages(response.data || []);
            setError('');
        } catch (err) {
            console.error('Error fetching accessible pages:', err);
            setError('Failed to load navigation');
            setPages([]);
        } finally {
            setLoading(false);
        }
    };

    const isMeetingsPage = (pageName) => {
        const name = pageName.toLowerCase();
        return name.includes('meeting');
    };

    const handlePageClick = (pageRoute, pageName) => {
        // Convert route to lowercase for URL consistency
        const lowercaseRoute = pageRoute.toLowerCase();

        // Close mobile menu when navigating
        setMobileMenuOpen(false);

        // If clicking Meetings and there are pending meetings, go to received tab
        if (isMeetingsPage(pageName) && pendingMeetingsCount > 0) {
            navigate(`/event/${eventNameId}${lowercaseRoute}/received`);
        } else {
            navigate(`/event/${eventNameId}${lowercaseRoute}`);
        }
    };

    // Close mobile menu when route changes
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Listen for toggle event from NavigationBar's hamburger button
    useEffect(() => {
        const handleToggleFromNavBar = () => {
            setMobileMenuOpen(prev => !prev);
        };
        window.addEventListener('toggleMobileNav', handleToggleFromNavBar);
        return () => window.removeEventListener('toggleMobileNav', handleToggleFromNavBar);
    }, []);

    // Calculate total badge count for mobile hamburger
    const totalBadgeCount = unreadCount + pendingMeetingsCount;

    // Send badge count updates to NavigationBar
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('mobileNavBadgeUpdate', {
            detail: { count: totalBadgeCount }
        }));
    }, [totalBadgeCount]);

    const isActivePage = (pageRoute) => {
        // Check both original and lowercase versions for active state
        const lowercaseRoute = pageRoute.toLowerCase();
        return location.pathname === `/event/${eventNameId}${lowercaseRoute}` ||
            location.pathname === `/event/${eventNameId}${pageRoute}`;
    };

    const getPageIcon = (pageName) => {
        const name = pageName.toLowerCase();
        const iconProps = { size: 18, strokeWidth: 2 };

        if (name.includes('home')) return <Home {...iconProps} />;
        if (name.includes('nonprofit') || name.includes('non-profit')) return <Heart {...iconProps} />;
        if (name.includes('philanthropist')) return <Handshake {...iconProps} />;
        if (name.includes('availability')) return <CalendarClock {...iconProps} />;
        if (name.includes('meeting')) return <CalendarCheck {...iconProps} />;
        if (name.includes('attendee')) return <Users {...iconProps} />;
        if (name.includes('agenda') || name.includes('schedule')) return <Calendar {...iconProps} />;
        if (name.includes('speaker')) return <Mic {...iconProps} />;
        if (name.includes('sponsor')) return <Award {...iconProps} />;
        if (name.includes('venue') || name.includes('location')) return <MapPin {...iconProps} />;
        if (name.includes('chat') || name.includes('message')) return <MessageCircle {...iconProps} />;
        if (name.includes('connection')) return <QrCode {...iconProps} />;
        if (name.includes('photo') || name.includes('gallery')) return <Camera {...iconProps} />;
        return <FileText {...iconProps} />; // Default icon
    };

    if (loading) {
        return (
            <div className="event-navigation">
                <div className="nav-loading">
                    <div className="spinner-small"></div>
                </div>
            </div>
        );
    }

    if (error && pages.length === 0) {
        return (
            <div className="event-navigation">
                <div className="nav-error">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    const isMessagesPage = (pageName) => {
        const name = pageName.toLowerCase();
        return name.includes('message') || name.includes('chat');
    };

    return (
        <>
            {/* Overlay for mobile menu */}
            {mobileMenuOpen && (
                <div
                    className="mobile-nav-overlay"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            <div className={`event-navigation ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                <nav className="nav-menu">
                    {pages.map((page) => (
                        <button
                            key={page.EventPageId}
                            className={`nav-item ${isActivePage(page.PageRoute) ? 'active' : ''}`}
                            onClick={() => handlePageClick(page.PageRoute, page.Name)}
                            style={{ position: 'relative' }}
                        >
                            <span className="nav-icon">{getPageIcon(page.Name)}</span>
                            <span className="nav-label">{page.Name}</span>
                            {isMessagesPage(page.Name) && unreadCount > 0 && (
                                <span style={{
                                    background: '#dc2626',
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    minWidth: '18px',
                                    textAlign: 'center',
                                    lineHeight: '14px',
                                    marginLeft: '8px'
                                }}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                            {isMeetingsPage(page.Name) && pendingMeetingsCount > 0 && (
                                <span style={{
                                    background: '#dc2626',
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    minWidth: '18px',
                                    textAlign: 'center',
                                    lineHeight: '14px',
                                    marginLeft: '8px'
                                }}>
                                    {pendingMeetingsCount > 99 ? '99+' : pendingMeetingsCount}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>
        </>
    );
};

export default EventNavigation;

